import React, { ReactNode, useMemo, useState } from 'react';
import { Button, CircularProgress, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';

import { tt } from 'i18n/tt';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import {
  TicketsParamsSelector,
  TicketsSelector,
  TicketsLoadingStateSelector,
} from 'store/tickets/selectors';
import { ThunkGetTickets } from 'store/tickets/thunks';

import Ticket from 'pages/Teams/components/Ticket';
import Input from 'components/Input/Input';
import ChatWithSupport from 'pages/Chats/dialogs/ChatWithSupport';
import { PortalConfigSelector } from 'store/config/selectors';
import { useSelector } from 'react-redux';
import CreateChatDialog from '../../Chats/dialogs/CreateChatDialog';

import { ReactComponent as SearchIcon } from 'assets/search.icon.svg';
import { ReactComponent as CloseIcon } from 'assets/cross.icon.svg';
import { ReactComponent as PlusIcon } from 'assets/add.icon.svg';
import { ReactComponent as SupportIcon } from 'assets/support.icon.svg';

import useStyles from '../styles';
import TicketsFilters from '../../Tickets/components/TicketsFilters/TicketsFilters';
import TicketsPagination from '../../Tickets/components/TicketsPagination/TicketsPagination';
import { changeTicketsParamsPage } from '../../../store/tickets';
import CreateTicketDialog from 'components/CreateTicketDialog';
import TechnicianVerificationTeams from '../../../components/Layout/components/TechnicianVerificationTeams';

const TicketsList: React.FC = () => {
  const [searchProcessing, setSearchProcessing] = useState(false);
  const [showChatWithSupport, setShowChatWithSupport] = useState<boolean>(false);
  const [openTicketCreationDialog, setOpenTicketCreationDialog] = useState<boolean>(false);

  const { useHelpdeskAi, disableChats } = useSelector(PortalConfigSelector);

  const dispatch = useAppDispatch();
  const tickets = useAppSelector(TicketsSelector);
  const params = useAppSelector(TicketsParamsSelector);
  const isTicketsLoading = useAppSelector(TicketsLoadingStateSelector);
  const classes = useStyles();
  const navigate = useNavigate();
  const [resetTrigger, setResetTrigger] = useState<boolean | undefined>();
  const searchFormik = useFormik({
    initialValues: {
      search: '',
    },
    onSubmit: values => {
      setSearchProcessing(true);
      dispatch(changeTicketsParamsPage(0));
      dispatch(
        ThunkGetTickets({
          isSearchAction: true,
          params: { ...params, page: 0, search: values.search },
        }),
      ).finally(() => {
        setSearchProcessing(false);
      });
    },
  });

  const inputIcon = useMemo(() => {
    if (searchProcessing) return <CircularProgress className={classes.searchIcon} />;

    if (!searchFormik.values.search) {
      return <SearchIcon className={classes.searchIcon} />;
    }

    return (
      <CloseIcon
        className={classes.searchIcon}
        onClick={() => {
          searchFormik.setFieldValue('search', '');
          searchFormik.handleSubmit();
        }}
      />
    );
  }, [searchFormik?.values?.search, searchProcessing]);

  const renderTickets = useMemo((): ReactNode => {
    if (isTicketsLoading) {
      return (
        <div className={classes.noTickets}>
          <CircularProgress />
        </div>
      );
    }

    if (tickets.length === 0) {
      return (
        <div className={classes.noTickets}>
          <Typography variant="body1">{tt('pages.teams.ticketsList.noTicketsFound')}</Typography>
        </div>
      );
    }

    return tickets.map(ticket => <Ticket {...ticket} key={ticket.ticketNumber ?? ticket.id} />);
  }, [isTicketsLoading, tickets]);

  return (
    <div className={classes.ticketsList}>
      <div className={classes.row}>
        <Typography variant="h5">
          <b>{tt('pages.teams.ticketsList.title')}</b>
        </Typography>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          {!disableChats && (
            <Button
              onClick={() => {
                setShowChatWithSupport(true);
              }}
              endIcon={<SupportIcon />}
              variant="contained"
            >
              {tt('pages.teams.ticketsList.chatWithSupport')}
            </Button>
          )}
          <Button
            onClick={() => {
              setOpenTicketCreationDialog(true);
            }}
            endIcon={<PlusIcon />}
            variant="contained"
          >
            {tt('pages.teams.ticketsList.createNewTicket')}
          </Button>
          <TechnicianVerificationTeams showAsButton />
        </div>
      </div>

      <div className={classes.column}>
        <form onSubmit={searchFormik.handleSubmit}>
          <Input
            size="small"
            formik={searchFormik}
            name="search"
            placeholder={tt('pages.teams.ticketsList.searchPlaceholder')}
            InputProps={{ endAdornment: inputIcon }}
          />
        </form>
        <TicketsFilters className={classes.filters} resetTrigger={resetTrigger} />
      </div>

      <div className={classes.list}>{renderTickets}</div>
      {!!tickets.length && <TicketsPagination />}
      {openTicketCreationDialog && (
        <CreateTicketDialog
          open={openTicketCreationDialog}
          onClose={() => {
            setOpenTicketCreationDialog(false);
          }}
          onSuccess={() => {
            setResetTrigger(prev => !prev);
            searchFormik.setFieldValue('search', '')
          }}
        />
      )}
      {useHelpdeskAi ? (
        <ChatWithSupport
          open={showChatWithSupport}
          onClose={() => {
            setShowChatWithSupport(false);
          }}
          onSubmit={() => {
            dispatch(changeTicketsParamsPage(0));
            dispatch(ThunkGetTickets({ params: { ...params, page: 0 } }));
          }}
        />
      ) : (
        <CreateChatDialog
          open={showChatWithSupport}
          onClose={() => {
            setShowChatWithSupport(false);
          }}
          onSubmit={(chatId, ticketId, ticketNumber) => {
            dispatch(ThunkGetTickets({ params })).then(() => {
              navigate(`/tickets/${ticketId || ticketNumber}?compactView=true&screen=chat`);
            });
          }}
        />
      )}
    </div>
  );
};

export default TicketsList;
